import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaChartBar, FaBrain, FaClipboardCheck, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const MoxoTesti = () => {
  const features = [
    {
      icon: <FaChartBar />,
      title: "Kapsamlı Analiz",
      description: "Dikkat ve performans düzeyinin detaylı değerlendirmesi"
    },
    {
      icon: <FaBrain />,
      title: "Bilişsel Ölçüm",
      description: "Çoklu görev ve dikkat kontrolü değerlendirmesi"
    },
    {
      icon: <FaClipboardCheck />,
      title: "Detaylı Rapor",
      description: "Sonuçların profesyonel yorumu ve öneriler"
    }
  ];

  return (
    <div className="min-h-screen bg-label">
      <Helmet>
        <title>MOXO Testi İstanbul | Dikkat Eksikliği ve Hiperaktivite Değerlendirmesi</title>
        <meta name="description" content="MOXO Dikkat Testi ile dikkat eksikliği ve hiperaktivite değerlendirmesi yapın. Bilimsel analiz ve detaylı rapor için randevu alın." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yagmurdemiroz.com/moxo-testi" />
        <meta property="og:title" content="MOXO Dikkat Testi - Bilimsel Dikkat Değerlendirmesi" />
        <meta property="og:description" content="Dikkat eksikliği ve hiperaktivite (DEHB) değerlendirmesi için bilimsel MOXO testi. Kapsamlı analiz ve detaylı rapor." />
        <meta property="og:image" content="https://yagmurdemiroz.com/me.jpg" />
        <meta property="og:locale" content="tr_TR" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalTest",
              "name": "MOXO Dikkat Testi",
              "description": "MOXO testi, dikkat eksikliği ve hiperaktivite bozukluğu (DEHB) tanısına yardımcı olan bilimsel bir testtir.",
              "provider": {
                "@type": "Psychologist",
                "name": "Psikolog Yağmur Demiröz",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "İstanbul, Türkiye"
                },
                "telephone": "+90 544 135 47 54"
              }
            }
          `}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center text-sm">
            <Link to="/" className="text-gray-600 hover:text-secondary">Ana Sayfa</Link>
            <FaChevronRight className="mx-2 text-gray-400" size={12} />
            <Link to="/calisma-alanlarim" className="text-gray-600 hover:text-secondary">Çalışma Alanlarım</Link>
            <FaChevronRight className="mx-2 text-gray-400" size={12} />
            <span className="text-secondary">MOXO Testi</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row items-start">
          <div className="w-full md:w-2/3 mb-6 md:mb-0">
            <h1 className="text-4xl font-bold text-secondary mb-6">
              MOXO Dikkat Testi – Dikkat ve Odaklanma Analizi
            </h1>
            
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                MOXO testi, dikkat eksikliği ve hiperaktivite bozukluğunun (DEHB) değerlendirilmesinde kullanılan bilgisayar tabanlı bir değerlendirme aracıdır.
              </p>

              <h2 className="text-2xl font-semibold text-secondary mb-4">
                MOXO Testi Nasıl Yapılır? Kimler İçin Uygundur?
              </h2>
              <p className="text-gray-700 mb-4">
                MOXO testi yaş gruplarına özel olarak tasarlanmış versiyonlara sahiptir:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700">
                <li className="mb-2">6-12 yaş arası çocuklar için özel test versiyonu</li>
                <li className="mb-2">13-65 yaş arası gençler ve yetişkinler için özel test versiyonu</li>
              </ul>
              <p className="text-gray-700 mb-6">
                Her yaş grubuna özel hazırlanmış test, aşağıdaki alanlarda değerlendirme yapar:
              </p>
              <ul className="list-disc list-inside mb-6 text-gray-700">
                <li className="mb-2">Dikkat düzeyi ölçümü</li>
                <li className="mb-2">Zamanlama ve tepki hızı analizi</li>
                <li className="mb-2">Dürtü kontrolü değerlendirmesi</li>
                <li className="mb-2">Hiperaktivite seviyesi ölçümü</li>
              </ul>

              <div className="grid md:grid-cols-3 gap-6 my-8">
                {features.map((feature, index) => (
                  <div key={index} className="bg-label p-6 rounded-lg border border-primary">
                    <div className="text-3xl text-secondary mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-semibold text-secondary mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-6 mb-8">
                <div className="bg-label p-4 rounded-lg border border-primary">
                  <Link to="/oyun-terapisi" className="text-secondary hover:text-primary transition-colors">
                    MOXO dikkat testi sonuçlarına göre bazı alanlarda destek ihtiyacı gözlemleniyorsa, oyun terapisi destekleyici bir yaklaşım olarak kullanılabilir.
                  </Link>
                </div>
                
                <div className="bg-label p-4 rounded-lg border border-primary">
                  <Link to="/ogrenci-danismanligi" className="text-secondary hover:text-primary transition-colors">
                    MOXO testi sonuçlarına göre akademik süreçlerde destek ihtiyacı belirlenen öğrenciler için öğrenci danışmanlığı planlanabilir.
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/3 md:ml-8">
            <div className="sticky top-24 bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-secondary mb-4">
                Test Randevusu
              </h3>
              <p className="text-gray-700 mb-6">
                MOXO testi hakkında bilgi almak ve randevu oluşturmak için iletişime geçebilirsiniz.
              </p>
              <a
                href="tel:+905441354754"
                className="w-full text-center inline-block bg-secondary text-label py-3 px-6 rounded-md hover:opacity-90 transition-all duration-300"
              >
                Randevu Alın
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoxoTesti;
